import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './hooks/useToast';
import { useWallet } from './WalletContext';
import './SingleDigitGame.css';

function StarlineSingleDigitGame({ gameName, onBack }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { walletBalance, updateWalletBalance } = useWallet();
  const [digit, setDigit] = useState('');
  const [points, setPoints] = useState('');
  const [bids, setBids] = useState([]);
  const [user, setUser] = useState(null);
  const [totalBids, setTotalBids] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [showBidPlaced, setShowBidPlaced] = useState(false);
  const [availableDigits, setAvailableDigits] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dropdownRef = useRef(null);

  // Get times and game data from location state
  const openTime = location.state?.openTime || '09:00';
  const closeTime = location.state?.closeTime || '18:00';
  const gameId = location.state?.gameId || '';
  const gameTypeFromState = location.state?.gameTypeId || '';
  const storedUser = localStorage.getItem('user');
  const playerId = storedUser ? JSON.parse(storedUser).id : '';

  // Fetch digits from API
  useEffect(() => {
    const fetchDigits = async () => {
      try {
        const response = await fetch('https://backend.mainbazar777.site/api/game-ank');
        const data = await response.json();
        if (data.game_ank && data.game_ank.length > 0) {
          setAvailableDigits(data.game_ank[0]);
        }
      } catch (err) {
        console.error('Failed to fetch digits:', err);
      }
    };

    fetchDigits();
  }, []);

  // Update suggestions when digit changes
  useEffect(() => {
    if (digit.trim()) {
      const singleDigits = availableDigits.single_digit;
      if (singleDigits) {
        let parsed = singleDigits;
        if (typeof singleDigits === 'string') {
          try {
            parsed = JSON.parse(singleDigits);
          } catch (e) {
            console.error('Error parsing single digits:', e);
            setSuggestions([]);
            return;
          }
        }

        const filtered = parsed
          .filter(d => d.toString().includes(digit.trim()))
          .slice(0, 10);
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [digit, availableDigits]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddBid = () => {
    if (!digit.trim() || !points.trim()) {
      toast.error('Please enter both digit and points');
      return;
    }

    const newBid = {
      id: Date.now(),
      pana: digit,
      point: points,
      type: 'Single'
    };

    setBids([...bids, newBid]);
    setTotalBids(totalBids + 1);
    setTotalPoints(totalPoints + parseInt(points));

    // Show bid placed notification
    setShowBidPlaced(true);
    setTimeout(() => setShowBidPlaced(false), 2000);

    // Clear inputs
    setDigit('');
    setPoints('');
  };

  const handleDeleteBid = (id) => {
    const bid = bids.find(b => b.id === id);
    setBids(bids.filter(b => b.id !== id));
    setTotalBids(totalBids - 1);
    setTotalPoints(totalPoints - parseInt(bid.point));
  };

  // Format time to 12-hour format
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${String(displayHour).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const isMarketClosed = () => {
    if (!closeTime) return false;
    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentTimeInMinutes = kolkataTime.getHours() * 60 + kolkataTime.getMinutes();
    const [ch, cm] = closeTime.split(':').map(Number);
    return currentTimeInMinutes >= ch * 60 + cm - 5;
  };

  const handleSubmit = async () => {
    if (isMarketClosed()) {
      toast.error('Market Closed');
      navigate('/starline');
      return;
    }
    // Validate inputs
    if (!digit.trim() || !points.trim()) {
      toast.error('Please enter both digit and funds');
      return;
    }

    // Check minimum bid amount is 10
    const pointsValue = parseInt(points);
    if (pointsValue < 10) {
      toast.error('Minimum bid amount is 10 frs');
      return;
    }

    // Validate required data
    if (!playerId) {
      toast.error('User not authenticated. Please login again.');
      return;
    }

    if (!gameId) {
      toast.error('Game information not available. Please select game again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare API payload using FormData
      const payload = new FormData();
      payload.append('game_id', gameId);
      payload.append('starline_game_type_id', gameTypeFromState);
      payload.append('player_id', playerId);
      payload.append('bid_amount', pointsValue.toString());
      payload.append('bid_number', digit);

      // Call the starline-play-game API
      const response = await fetch('https://backend.mainbazar777.site/api/starline-play-game', {
        method: 'POST',
        body: payload
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Show success notification
        setShowBidPlaced(true);
        setTimeout(() => setShowBidPlaced(false), 2000);
        
        toast.success(`Bid Placed: Number ${digit}, Amount ${pointsValue} frs`);
        
        // Refresh wallet balance after successful bid
        if (playerId) {
          updateWalletBalance(playerId);
        }
        
        // Clear inputs
        setDigit('');
        setPoints('');
      } else {
        toast.error(data.message || 'Failed to place bid. Please try again.');
      }
    } catch (err) {
      console.error('API Error:', err);
      toast.error('Error placing bid. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date();
  const dateString = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;

  return (
    <div className="single-digit-game">
      {/* Header */}
      <header className="single-digit-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="game-title">{gameName || 'SINGLE DIGIT'}</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </header>

      {/* Main Content */}
      <div className="game-content">
        {/* Game Info Section - No Status for Starline */}
        <div className="game-info-section">
          <h2 className="game-type-heading">Single Digit</h2>
          <div className="info-row">
            <div className="info-item">
              <label>Date</label>
              <input type="text" value={dateString} readOnly disabled />
            </div>
          </div>

          <div className="time-section">
            <div className="time-item">
              <span className="time-label">Time Open:</span>
              <span className="time-value">{formatTime(openTime)}</span>
            </div>
            <div className="time-item">
              <span className="time-label">Time Close:</span>
              <span className="time-value">{formatTime(closeTime)}</span>
            </div>
          </div>
        </div>

        {/* Input Section */}
        <div className="input-section">
          <div className="input-row">
            <div className="input-item" ref={dropdownRef}>
              <label>Enter Digit:</label>
              <div className="dropdown-container">
                <input
                  type="text"
                  placeholder="Single digit"
                  value={digit}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Only allow numbers
                    value = value.replace(/[^0-9]/g, '');
                    // Limit to 1 digit
                    value = value.slice(0, 1);
                    setDigit(value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  maxLength={1}
                  className="digit-input"
                  inputMode="numeric"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="dropdown-menu">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="dropdown-item"
                        onClick={() => {
                          setDigit(suggestion.toString());
                          setShowSuggestions(false);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="input-item">
              <label>Enter Funds: <span className="min-bet">(Min: 10)</span></label>
              <input
                type="number"
                placeholder="Min 10 frs"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="points-input"
                min="10"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button className="submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Placing Bid...' : 'Place Bid'}
        </button>
      </div>

      {/* Bid Placed Notification */}
      {showBidPlaced && (
        <div className="bid-placed-notification">
          ✓ Bid Placed Successfully
        </div>
      )}
    </div>
  );
}

export default StarlineSingleDigitGame;
